import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';

const AddSubjectLevelClass = () => {
  const [formData, setFormData] = useState({
    ClassName: '',
    Subject: '',
    Teacher: '',
    TeacherName: '',
    Form: '',
    ClassType: '',
    gradelevelclass: '',
  });
  const [teachers, setTeachers] = useState([]);
  const [gradeLevelClasses, setGradeLevelClasses] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '' });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchTeachers(), fetchGradeLevelClasses(), fetchClassTypes()]);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/createemployee/teachers/teachers`);
      setTeachers(response.data);
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to fetch teachers.',
      });
    }
  };

  const fetchGradeLevelClasses = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/getgradelevelclass`);
      setGradeLevelClasses(response.data.classes);
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to fetch grade level classes.',
      });
    }
  };

  const fetchClassTypes = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/class-types`);
      setClassTypes(response.data);
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to fetch class types.',
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTeacherChange = (e) => {
    const selectedTeacher = teachers.find((teacher) => teacher.EmployeeNumber === e.target.value);
    setFormData({
      ...formData,
      Teacher: selectedTeacher ? selectedTeacher.EmployeeNumber : '',
      TeacherName: selectedTeacher ? `${selectedTeacher.name} ${selectedTeacher.surname}` : '',
    });
  };

  const handleGradeLevelClassChange = (e) => {
    const selectedClassID = e.target.value;
    const selectedGradeLevelClass = gradeLevelClasses.find(
      (gradeLevelClass) => gradeLevelClass.ClassID === parseInt(selectedClassID, 10)
    );

    if (selectedGradeLevelClass) {
      setFormData({
        ...formData,
        gradelevelclass: selectedClassID,
        Form: selectedGradeLevelClass.Form,
        ClassType: selectedGradeLevelClass.ClassType,
      });
    } else {
      setFormData({
        ...formData,
        gradelevelclass: '',
        Form: '',
        ClassType: '',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const subjectClassData = {
        ...formData,
        gradelevelclass: formData.gradelevelclass,
      };

      await axios.post(`${BASE_URL}/subjectlevelclass`, subjectClassData);
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Subject level class created successfully!',
      });

      // Clear inputs after success
      setFormData({
        ClassName: '',
        Subject: '',
        Teacher: '',
        TeacherName: '',
        Form: '',
        ClassType: '',
        gradelevelclass: '',
      });
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: "Error creating subject level class. Either the class exists or there's a network issue.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setModal({ isOpen: false, title: '', message: '' });
  };

  // Filter class types based on the selected Form
  const filteredClassTypes = classTypes.filter(type => {
    if (formData.Form >= 5) {
      return ['Gold', 'Purple', 'Silver', 'Sciences', 'Arts', 'Commercials'].includes(type.ClassTypeName);
    } else {
      return ['Maroon', 'Gray', 'White', 'Red', 'Orange', 'Green', 'Blue'].includes(type.ClassTypeName);
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 py-2">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg p-6">
          <div className="mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Create New Subject Level Class</h2>
              <div className="h-1 w-full bg-gray-200 mt-2"></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Grade Level Class</label>
                <select
                  name="GradeLevelClassID"
                  value={formData.gradelevelclass}
                  onChange={handleGradeLevelClassChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Grade Level Class</option>
                  {Array.isArray(gradeLevelClasses) && gradeLevelClasses.map((gradeLevelClass) => (
                    <option key={gradeLevelClass.ClassID} value={gradeLevelClass.ClassID}>
                      {gradeLevelClass.ClassName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Class Name</label>
                <input
                  type="text"
                  name="ClassName"
                  value={formData.ClassName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  placeholder="Enter class name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Form</label>
                <input
                  type="text"
                  name="Form"
                  value={formData.Form}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors bg-gray-50"
                  required
                  disabled
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Class Type</label>
                <select
                  name="ClassType"
                  value={formData.ClassType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors bg-gray-50"
                  required
                  disabled
                >
                  <option value="">Select Class Type</option>
                  {filteredClassTypes.map((type) => (
                    <option key={type.id} value={type.ClassTypeName}>
                      {type.ClassTypeName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  name="Subject"
                  value={formData.Subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  placeholder="Enter subject name"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Teacher</label>
                <select
                  name="Teacher"
                  value={formData.Teacher}
                  onChange={handleTeacherChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Teacher</option>
                  {Array.isArray(teachers) && teachers.map((teacher) => (
                    <option key={teacher.EmployeeNumber} value={teacher.EmployeeNumber}>
                      {`${teacher.name} ${teacher.surname}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Term</label>
                <select
                  name="Term"
                  value={formData.Term}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Term</option>
                  {[1, 2, 3].map((term) => (
                    <option key={term} value={term}>
                      Term {term}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                <select
                  name="Year"
                  value={formData.Year}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Year</option>
                  {Array.from({ length: 78 }, (_, i) => 2023 + i).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                className="bg-pink-600 text-white px-6 py-3 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Class'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md mx-auto transform transition-all">
            <h3 className={`text-xl font-semibold mb-4 ${modal.title === 'Error' ? 'text-red-600' : 'text-green-600'}`}>
              {modal.title}
            </h3>
            <p className="text-gray-700">{modal.message}</p>
            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSubjectLevelClass;
